import React, {useCallback} from "react";
import {TASK_STATUS_ENUM} from "constraints/taskd";
import {IStandTaskListRow} from "ts/api/stand-task-list-row";
import {useSnackbar} from "notistack";
import {useTranslate} from "@tolgee/react";
import CloseSnackbarButton from "view/components/Snackbar/notistack/CloseButton";
import caseInsensitiveSort from "tools/common/caseInsensitiveSort";
import {useTheme} from "@material-ui/core";
import {IOrganiser, TaskStatus} from "@evennt-tech/api-v2-core";

type TranslateFn = ReturnType<typeof useTranslate>["t"];

export const getStatus = (row?: IStandTaskListRow): string => {
  if (row?.instance?.isRejected) {
    return "Rejected";
  }
  if (row?.instance?.isInRevision) {
    return TASK_STATUS_ENUM.revision;
  }
  const status = row?.instance?.status ?? TaskStatus.TODO;
  // Draft = response started but not submitted; the instance stays in
  // TODO, so surface the underlying status alongside the draft marker.
  if (row?.isDraft && status === TaskStatus.TODO) {
    return `${TASK_STATUS_ENUM[TaskStatus.TODO]} (Draft)`;
  }
  return TASK_STATUS_ENUM[status];
};

const STATUS_LABEL: Record<TaskStatus, [key: string, fallback: string]> = {
  [TaskStatus.TODO]: ["common.tasks.todo", TASK_STATUS_ENUM.todo],
  [TaskStatus.IN_REVIEW]: [
    "common.tasks.status.in_review",
    TASK_STATUS_ENUM.inReview,
  ],
  [TaskStatus.WAITING_FOR_APPROVAL]: [
    "common.tasks.waiting_approval",
    TASK_STATUS_ENUM.waitingForApproval,
  ],
  [TaskStatus.DONE]: ["common.tasks.done", TASK_STATUS_ENUM.done],
};

/**
 * Translated display text for the same state `getStatus` describes. Kept
 * separate because callers still compare `getStatus`'s English string to drive
 * actions.
 */
export const getStatusLabel = (
  t: TranslateFn,
  row?: IStandTaskListRow
): string => {
  if (row?.instance?.isRejected) {
    return t("common.tasks.status.rejected", "Rejected");
  }
  if (row?.instance?.isInRevision) {
    return t(
      "common.tasks.status.revision_required",
      TASK_STATUS_ENUM.revision
    );
  }
  const status = row?.instance?.status ?? TaskStatus.TODO;
  const entry = STATUS_LABEL[status] as
    | [key: string, fallback: string]
    | undefined;
  if (!entry) {
    return TASK_STATUS_ENUM[status] ?? status;
  }
  const [key, fallback] = entry;
  const label = t(key, fallback);
  if (row?.isDraft && status === TaskStatus.TODO) {
    return t("common.tasks.status.draft", "{status} (Draft)", {status: label});
  }
  return label;
};

export const getStatusTextColor = (row?: IStandTaskListRow): string => {
  const theme = useTheme();
  const status = row?.instance?.status;
  if (row?.instance?.isRejected) return theme.palette.error.main;
  if (row?.isDraft && (status ?? TaskStatus.TODO) === TaskStatus.TODO)
    return theme.palette.text.secondary;
  if (status === TaskStatus.WAITING_FOR_APPROVAL)
    return theme.palette.warning.main;
  if (status === TaskStatus.IN_REVIEW) return theme.palette.warning.main;
  if (status === TaskStatus.DONE) return theme.palette.success.main;
  return theme.palette.contrastText.main;
};

export const useGetShowSubmittedMessage = () => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {t} = useTranslate();
  return useCallback(() => {
    enqueueSnackbar(t("common.tasks.response_modal.submitted_success"), {
      variant: "success",
      action: (snackbarId) => (
        <CloseSnackbarButton onClick={() => closeSnackbar(snackbarId)} />
      ),
    });
  }, [enqueueSnackbar, closeSnackbar, t]);
};

const mapToOrganiserOption = (organiser: IOrganiser) => ({
  label: organiser.contactPerson,
  value: organiser.id,
  company:
    organiser?.organiserCompanyToOrganisers?.[0]?.organiserCompany?.name ||
    null,
});

export const getOrganiserOptions = (organisers: IOrganiser[]) => {
  const options = organisers.map(mapToOrganiserOption);
  return caseInsensitiveSort(options, "contactPerson");
};
